import { HTMLAttributes, KeyboardEvent, PropsWithChildren, forwardRef, memo, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { CalendarEvent } from '../../types';
import { getEffectiveDuration, timeToRowNumber } from '../../utils';

// visual-only bleed (px) so cross-day events poke slightly outside their day boundary, mirroring the legacy timeline view
const CROSS_DAY_BLEED_PX = 6;

interface Props
  extends Pick<CalendarEvent, 'id' | 'startTime' | 'duration' | 'continuesFromPreviousDay' | 'continuesToNextDay'>,
    Omit<HTMLAttributes<HTMLElement>, 'id' | 'style'> {
  style?: React.CSSProperties;
  isSelectable?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
  contentOverflow?: 'hidden' | 'visible';
}

const EventShellComponent = forwardRef<HTMLElement, PropsWithChildren<Props>>(
  (
    {
      children,
      id,
      startTime,
      duration,
      continuesFromPreviousDay,
      continuesToNextDay,
      style,
      isSelected,
      isSelectable,
      onSelect,
      contentOverflow = 'hidden',
      ...rest
    },
    ref
  ) => {
    const mergedInlineStyle = useMemo(
      () =>
        ({
          '--start-row': String(timeToRowNumber(startTime)),
          '--duration-in-minutes': String(getEffectiveDuration(duration)),
          ...style,
        } as React.CSSProperties),
      [duration, startTime, style]
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLElement>) => {
        if (!isSelectable || !onSelect) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        onSelect();
      },
      [isSelectable, onSelect]
    );

    return (
      <article
        ref={ref}
        data-event-id={id}
        data-event-selected={isSelected ? 'true' : undefined}
        data-continues-from-previous-day={continuesFromPreviousDay ? 'true' : undefined}
        data-continues-to-next-day={continuesToNextDay ? 'true' : undefined}
        data-content-overflow={contentOverflow}
        css={containerStyle}
        style={mergedInlineStyle}
        role={isSelectable ? 'button' : undefined}
        tabIndex={isSelectable ? 0 : undefined}
        onClick={isSelectable ? onSelect : undefined}
        onKeyDown={isSelectable ? handleKeyDown : undefined}
        onMouseDown={(e) => e.stopPropagation()} // to avoid drag create selection from being triggered
        {...rest}
      >
        {children}
      </article>
    );
  }
);

EventShellComponent.displayName = 'EventShell';

export const EventShell = memo(EventShellComponent);

const containerStyle = css({
  borderRadius: 'var(--fabric-radius-sm)',
  gridRow: 'var(--start-row) / span var(--duration-in-minutes)',
  gridColumn: 1,
  // Overrides the default auto min-width so long unbreakable content (e.g. names) truncates instead of widening the card
  minWidth: 0,
  overflow: 'visible',
  willChange: 'transform',
  cursor: 'default',
  '&[role="button"]': {
    cursor: 'pointer',
  },
  '> *': {
    height: '100%',
    overflow: 'hidden',
  },
  '&[data-content-overflow="visible"] > *': {
    overflow: 'visible',
  },
  // bleed the card slightly above its own row so it reads as continuing from the previous day
  '&[data-continues-from-previous-day="true"]': {
    marginTop: -CROSS_DAY_BLEED_PX,
  },
  '&[data-continues-from-previous-day="true"] > *, &[data-continues-to-next-day="true"] > *': {
    height: `calc(100% + ${CROSS_DAY_BLEED_PX}px)`,
  },
});
